/**
 * Add your config changes here.
 * @module config
 * @example
 * export const settings = {
 *   ...config.settings,
 *   port: 4300,
 *   listBlockTypes: {
 *     ...config.settings.listBlockTypes,
 *     'my-list-item',
 *   }
 * }
 */
import React from 'react';
import { defineMessages } from 'react-intl';
import createBlockStyleButton from 'draft-js-buttons/lib/utils/createBlockStyleButton';
import createBlockBreakoutPlugin from 'draft-js-block-breakout-plugin';
import { Map } from 'immutable';
import { DefaultDraftBlockRenderMap } from 'draft-js';
import loadable from '@loadable/component';

import {
  richtextEditorSettings,
  richtextViewSettings,
} from './components/RichTextEditor';

import DefaultView from '@plone/volto/components/theme/View/DefaultView';
import WysiwygWidget from '@plone/volto/components/manage/Widgets/WysiwygWidget';

// Circular import problem, so pointing it right to the component
import Icon from '@plone/volto/components/theme/Icon/Icon';

import reducers from './reducers';

import {
  AlbumView,
  ContactPersonListing,
  ContactPersonsView,
  ContactSalesServiceListing,
  VerkaufskonceptAlbumView,
  Verkaufskonzept,
  ViewTextBlock,
  ViewHeroBlock,
  ViewHero2Block,
  ViewHero3Block,
  ViewPassendeProdukteBlock,
  ViewPassendeRezepteBlock,
  ViewCountdownBlock,
  EditTextBlock,
  EditHeroBlock,
  EditHero2Block,
  EditHero3Block,
  EditPassendeProdukteBlock,
  EditPassendeRezepteBlock,
  EditCountdownBlock,
  SocialMediaView,
  GridAlbumView,
  EditPassendeProdukteBlockNew,
  ViewPassendeProdukteBlockNew,
  EditPassendeRezepteBlockNew,
  ViewPassendeRezepteBlockNew,
  TextSettingsSchema,
} from './components';

import textSVG from '@plone/volto/icons/subtext.svg';
import heroBlockSVG from './icons/block1.svg';
import heroBlock2SVG from './icons/block2.svg';
import heroBlock3SVG from './icons/block3.svg';
import passendeProdukteBlockSVG from './icons/block4.svg';
import passendeRezepteBlockSVG from './icons/block5.svg';
import checkmarkslistSVG from './icons/properties.svg';
import arrowlistSVG from './icons/list-arrows.svg';

import {
  Aktuelles,
  Homepage,
  Merkliste,
  OnapplyJobsView,
  Product,
  ProductList,
  Recipe,
  Bilddatenbank,
  MediathekView,
  InxMailRegister,
  MailError,
  MailSuccess,
  PasswordProtectedView,
} from './components';
import {
  Rezeptdatenbank,
  RohstoffeListing,
  BenutzerListing,
  BenutzerDetails,
  BenutzerCreate,
  BenutzerEdit,
  RohstoffeDetails,
  RohstoffCreate,
  RohstoffEdit,
  ProdukteListing,
  ProdukteDetails,
  ProduktCreate,
  ProduktEdit,
  GebindeCreate,
  GebindeEdit,
  RezepteListing,
  RezepteDetails,
  RezeptCreate,
  RezeptEdit,
  BezugCreate,
  BezuegeListing,
  BezugEdit,
  ZutatengruppenListing,
  ZutatengruppeCreate,
  ZutatengruppeEdit,
  VerkaufsinformationEdit,
  GruppenListing,
  GruppeCreate,
  GruppeEdit,
  RezeptkategorienListing,
  RezeptkategorieCreate,
  RezeptkategorieEdit,
  RezeptgruppenListing,
  RezeptgruppeCreate,
  RezeptgruppeEdit,
  ProduktkategorienListing,
  ProduktkategorieCreate,
  ProduktkategorieEdit,
  RohstoffkategorienListing,
  RohstoffkategorieCreate,
  RohstoffkategorieEdit,
} from './components/Rezeptdatenbank';

// // All your imports required for the config here BEFORE this line
// import '@plone/volto/config';

// Views

const layoutViews = {
  album_view: AlbumView,
  verkaufskonzept_album_view: VerkaufskonceptAlbumView,
  ihre_ansprechpartner_view: ContactPersonsView,
  ihre_ansprechpartner_listing_view: ContactPersonListing,
  ihre_ansprechpartner_verkaufsservice_listing_view: ContactSalesServiceListing,
  grid_album_view: GridAlbumView,
};

const contentTypesViews = {
  'News Item': DefaultView,
  'jungzeelandia.SalesConcept': Verkaufskonzept,
  Download: SocialMediaView,
};

// Blocks
const blockRenderMap = Map({
  callout: {
    element: 'p',
  },
  unstyled: {
    element: 'p',
  },
  arrowList: {
    element: 'li',
    wrapper: <ul className="arrow-list" />,
  },
  checkmarksList: {
    element: 'li',
    wrapper: <ul className="checkmarks-list" />,
  },
});
const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);

const blockStyleFn = (contentBlock) => {
  const type = contentBlock.getType();
  if (type === 'callout') {
    return 'callout';
  }
  if (type === 'arrowList') {
    return 'arrow-list';
  }
  if (type === 'checkmarksList') {
    return 'checkmarks-list';
  }
  return null;
};

const listBlockTypes = ['arrowList', 'checkmarksList'];

// Inline Toolbar custom buttons
const ArrowListButton = createBlockStyleButton({
  blockType: 'arrowList',
  children: <Icon name={arrowlistSVG} size="24px" />,
});

const CheckMarksListButton = createBlockStyleButton({
  blockType: 'checkmarksList',
  children: <Icon name={checkmarkslistSVG} size="24px" />,
});

// Plugins
const breakOutOptions = {
  doubleBreakoutBlocks: [
    'unordered-list-item',
    'ordered-list-item',
    'code-block',
    'arrowList',
    'checkmarksList',
  ],
  breakoutBlocks: [
    'header-one',
    'header-two',
    'header-three',
    'highlight',
    'blockquote',
    'callout',
  ],
};
const blockBreakoutPlugin = createBlockBreakoutPlugin(breakOutOptions);

// FromHTML
const FromHTMLCustomBlockFn = (element) => {
  if (element.className === 'callout') {
    return {
      type: 'callout',
    };
  }
  if (element.className === 'arrow-list-item') {
    return {
      type: 'arrowList',
    };
  }
  if (element.className === 'checkmarks-list-item') {
    return {
      type: 'checkmarksList',
    };
  }
  return null;
};

// ToHTML
// Returns how the default lists should be rendered
const getList =
  (ordered) =>
  (children, { depth, keys }) =>
    ordered ? (
      <ol className="tile" key={keys[0]} keys={keys} depth={depth}>
        {children.map((child, i) => (
          <li key={keys[i]}>{child}</li>
        ))}
      </ol>
    ) : (
      <ul className="tile" key={keys[0]} keys={keys} depth={depth}>
        {children.map((child, i) => (
          <li key={keys[i]}>{child}</li>
        ))}
      </ul>
    );

// Special function to deal with list clones
const getSpecialList =
  (type) =>
  (children, { depth, keys }) => (
    <ul key={keys[0]} keys={keys} depth={depth} className={`tile ${type}`}>
      {children.map((child, i) => (
        <li key={keys[i]} className={`${type}-item`}>
          {child}
        </li>
      ))}
    </ul>
  );

const blocks = {
  unstyled: (children, { keys }) =>
    children.map((child) => (
      <p className="tile text" key={keys[0]}>
        {child}
      </p>
    )),
  callout: (children, { keys }) =>
    children.map((child, i) => (
      <p key={keys[i]} className="tile callout">
        {child}
      </p>
    )),
  'unordered-list-item': getList(),
  'ordered-list-item': getList(true),
  arrowList: getSpecialList('arrow-list'),
  checkmarksList: getSpecialList('checkmarks-list'),
};

defineMessages({
  title: {
    id: 'title',
    defaultMessage: 'Title',
  },
  description: {
    id: 'description',
    defaultMessage: 'Description',
  },
  text: {
    id: 'text',
    defaultMessage: 'Text',
  },
  image: {
    id: 'image',
    defaultMessage: 'Image',
  },
  video: {
    id: 'video',
    defaultMessage: 'Video',
  },
  hero: {
    id: 'hero',
    defaultMessage: 'Hero',
  },
  hero2: {
    id: 'hero2',
    defaultMessage: 'Hero 2',
  },
  hero3: {
    id: 'hero3',
    defaultMessage: 'Hero 3',
  },
  passendeProdukte: {
    id: 'passendeProdukte',
    defaultMessage: 'Passende Produkte',
  },
  passendeRezepte: {
    id: 'passendeRezepte',
    defaultMessage: 'Passende Rezepte',
  },
  countdown: {
    id: 'countdown',
    defaultMessage: 'Countdown',
  },
  label_my_username_is: {
    id: 'label_my_username_is',
    defaultMessage: 'Meine Email Addresse lautet:',
  },
  loginFailedContent: {
    id: 'Both email address and password are case sensitive, check that caps lock is not enabled.',
    defaultMessage:
      'Both email address and password are case sensitive, check that caps lock is not enabled.',
  },
  loginFailed: {
    id: 'Login Failed',
    defaultMessage: 'Login Failed',
  },
});

// Non content routes
const nonContentRoutes = [
  /\/alle-rezepte\/.*$/,
  /\/alle-produkte\/.*$/,
  '/alle-rezepte',
  '/alle-produkte',
  '/mail-register',
  '/mail-error',
  '/mail-success',
];

export default function applyConfig(config) {
  /**
   * Settings
   */

  config.addonReducers = { ...config.addonReducers, ...reducers };

  config.settings.nonContentRoutes.push(...nonContentRoutes);
  config.settings.defaultLanguage = 'de';
  config.settings.supportedLanguages = ['de'];

  config.settings.richtextEditorSettings = richtextEditorSettings;
  config.settings.richtextViewSettings = richtextViewSettings;

  // Rich Text settings
  config.settings.richtextEditorSettings = (props) => {
    const result = richtextEditorSettings(props);

    result.richTextEditorInlineToolbarButtons.push(
      ...[ArrowListButton, CheckMarksListButton],
    );
    result.richTextEditorPlugins = [
      result.richTextEditorPlugins[0],
      blockBreakoutPlugin,
    ];
    result.extendedBlockRenderMap = extendedBlockRenderMap;
    result.blockStyleFn = blockStyleFn;
    result.FromHTMLCustomBlockFn = FromHTMLCustomBlockFn;
    result.listBlockTypes.push(...listBlockTypes);

    return result;
  };

  config.settings.richtextViewSettings.ToHTMLRenderers.blocks = {
    ...config.settings.richtextViewSettings.ToHTMLRenderers.blocks,
    ...blocks,
  };
  config.settings.richtextViewSettings.ToHTMLRenderers.entities = {
    LINK: (children, entity, { key }) => {
      const url =
        entity.url.substr(entity.url.length - 4).toLowerCase() === '.pdf'
          ? `${config.settings.apiPath}${entity.url}/@@download/file`
          : entity.url;
      return (
        <a key={key} href={url}>
          {children}
        </a>
      );
    },
  };

  config.settings.DEFAULT_PAGING_SIZE = 12;
  config.settings.defaultPageSize = 12;
  config.settings.DEFAULT_NEWS_PAGING_SIZE = 6;
  config.settings.DEFAULT_GRIDALBUM_PAGING_SIZE = 12;
  config.settings.DEFAULT_SEARCHABLE_TYPES = [
    'Document',
    'Page',
    'jungzeelandia.Product',
    'jungzeelandia.Recipe',
    'jungzeelandia.SalesConcept',
    'News Item',
    'File',
  ];
  config.settings.typeLiteralMap = (type) => {
    switch (type) {
      case 'jungzeelandia.Recipe':
        return 'Rezepte';
      case 'jungzeelandia.Product':
        return 'Produkt';
      case 'jungzeelandia.SalesConcept':
        return 'Verkaufskonzept';
      case 'Document':
      case 'Page':
        return 'Beitrag';
      case 'Image':
        return 'Bild';
      case 'News Item':
        return 'Presse';
      case 'File':
        return 'Dokument';
      default:
        return type;
    }
  };

  config.settings.ONAPPLY_URL =
    'https://zeelandia.onapply.de/feed/render.html?format=json';

  // draftjs loadables
  config.settings.loadables.immutableLib = loadable.lib(
    () => import('immutable'),
  );
  config.settings.loadables.draftJs = loadable.lib(() => import('draft-js'));
  config.settings.loadables.draftJsLibIsSoftNewlineEvent = loadable.lib(
    () => import('draft-js/lib/isSoftNewlineEvent'),
  );
  config.settings.loadables.draftJsFilters = loadable.lib(
    () => import('draftjs-filters'),
  );
  config.settings.loadables.draftJsInlineToolbarPlugin = loadable.lib(
    () => import('draft-js-inline-toolbar-plugin'),
  );
  config.settings.loadables.draftJsImportHtml = loadable.lib(
    () => import('draft-js-import-html'),
  );
  config.settings.loadables.draftJsBlockBreakoutPlugin = loadable.lib(
    () => import('draft-js-block-breakout-plugin'),
  );
  config.settings.loadables.draftJsCreateInlineStyleButton = loadable.lib(
    () => import('draft-js-buttons/lib/utils/createInlineStyleButton'),
  );
  config.settings.loadables.draftJsCreateBlockStyleButton = loadable.lib(
    () => import('draft-js-buttons/lib/utils/createBlockStyleButton'),
  );
  config.settings.loadables.draftJsPluginsUtils = loadable.lib(
    () => import('draft-js-plugins-utils'),
  );

  /**
   * Views
   */
  config.views.layoutViews = {
    ...config.views.layoutViews,
    ...layoutViews,
  };

  config.views.layoutViewsNamesMapping = {
    ...config.views.layoutViewsNamesMapping,
    grid_album_view: 'Grid Album View',
  };

  config.views.contentTypesViews = {
    ...config.views.contentTypesViews,
    ...contentTypesViews,
  };

  /**
   * Blocks
   */

  config.blocks.groupBlocksOrder = [
    ...config.blocks.groupBlocksOrder,
    { id: 'related', title: 'Produkte / Rezepte' },
  ];

  config.settings.defaultBlockType = 'text';
  config.widgets.widget.richtext = WysiwygWidget;
  config.blocks.blocksConfig.description.restricted = false;
  config.blocks.blocksConfig.slateTable.restricted = true;
  config.blocks.blocksConfig.slate.restricted = true;
  config.blocks.blocksConfig = {
    ...config.blocks.blocksConfig,
    text: {
      id: 'text',
      title: 'Text',
      icon: textSVG,
      group: 'text',
      view: ViewTextBlock,
      edit: EditTextBlock,
      schema: TextSettingsSchema,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: true,
      sidebarTab: 0,
      blockHasValue: (data) => {
        const isEmpty =
          !data.text ||
          (data.text?.blocks?.length === 1 && data.text.blocks[0].text === '');
        return !isEmpty;
      },
    },
    countdown: {
      id: 'countdown',
      title: 'Countdown',
      icon: passendeRezepteBlockSVG,
      group: 'common',
      view: ViewCountdownBlock,
      edit: EditCountdownBlock,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: true,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },
    hero: {
      id: 'hero',
      title: 'Hero',
      icon: heroBlockSVG,
      group: 'common',
      view: ViewHeroBlock,
      edit: EditHeroBlock,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: true,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },
    hero2: {
      id: 'hero2',
      title: 'Hero 2',
      icon: heroBlock2SVG,
      group: 'common',
      view: ViewHero2Block,
      edit: EditHero2Block,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: true,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },
    hero3: {
      id: 'hero3',
      title: 'Hero 3',
      icon: heroBlock3SVG,
      group: 'common',
      view: ViewHero3Block,
      edit: EditHero3Block,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: true,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },
    passendeProdukteSelect: {
      id: 'passendeProdukteSelect',
      title: 'Passende Produkte',
      icon: passendeProdukteBlockSVG,
      group: 'related',
      view: ViewPassendeProdukteBlockNew,
      edit: EditPassendeProdukteBlockNew,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: true,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },
    passendeRezepteSelect: {
      id: 'passendeRezepteSelect',
      title: 'Passende Rezepte',
      icon: passendeRezepteBlockSVG,
      group: 'related',
      view: ViewPassendeRezepteBlockNew,
      edit: EditPassendeRezepteBlockNew,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: true,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },
    passendeProdukte: {
      id: 'passendeProdukte',
      title: 'Passende Produkte (nach Kategorie)',
      icon: passendeProdukteBlockSVG,
      group: 'related',
      view: ViewPassendeProdukteBlock,
      edit: EditPassendeProdukteBlock,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: true,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },
    passendeRezepte: {
      id: 'passendeRezepte',
      title: 'Passende Rezepte (nach Kategorie)',
      icon: passendeRezepteBlockSVG,
      group: 'related',
      view: ViewPassendeRezepteBlock,
      edit: EditPassendeRezepteBlock,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: true,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },
  };

  config.settings.DSGVOBanner = {
    ...config.settings.DSGVOBanner,
    tracker: {
      type: 'matomo',
      id: 2,
      urlBase: 'https://stats.zeelandia.de/',
    },
    modules: ['tracking'],
    privacy_url: '/datenschutz',
  };

  config.settings.pd_path = '/produktdatenblaetter';

  config.addonRoutes = [
    ...config.addonRoutes,
    {
      path: '/wir-ueber-uns/aktuelles',
      component: Aktuelles,
      exact: true,
    },
    {
      path: '/wir-ueber-uns/karriere/stellenangebote',
      component: OnapplyJobsView,
      exact: true,
    },
    {
      path: '/',
      component: Homepage,
      exact: true,
    },
    {
      path: '/merkliste',
      component: Merkliste,
    },
    {
      path: '/alle-produkte',
      component: ProductList,
      exact: true,
    },
    {
      path: '/alle-produkte/:id',
      component: Product,
    },
    {
      path: '/alle-rezepte',
      component: ProductList,
      exact: true,
    },
    {
      path: '/alle-rezepte/:id',
      component: Recipe,
    },
    {
      path: '/kompetenzen/mediathek/bilddatenbank',
      component: Bilddatenbank,
      exact: true,
    },
    {
      path: '/kompetenzen/mediathek',
      component: MediathekView,
      exact: true,
    },
    {
      path: '/kompetenzen/mediathek/digitale-vorlagen',
      component: PasswordProtectedView,
      exact: true,
    },
    {
      path: '/mail-register',
      component: InxMailRegister,
      exact: true,
    },
    {
      path: '/mail-error',
      component: MailError,
      exact: true,
    },
    {
      path: '/mail-success',
      component: MailSuccess,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank',
      component: Rezeptdatenbank,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rohstoffe',
      component: RohstoffeListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rohstoffe/add',
      component: RohstoffCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rohstoffe/edit',
      component: RohstoffEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rohstoffe/details',
      component: RohstoffeDetails,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/benutzer',
      component: BenutzerListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/benutzer/details',
      component: BenutzerDetails,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/benutzer/add',
      component: BenutzerCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/benutzer/edit',
      component: BenutzerEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/produkte',
      component: ProdukteListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/produkte/details',
      component: ProdukteDetails,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/produkte/add',
      component: ProduktCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/produkte/edit',
      component: ProduktEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/produkte/gebinde/add',
      component: GebindeCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/produkte/gebinde/edit',
      component: GebindeEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezepte',
      component: RezepteListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezepte/details',
      component: RezepteDetails,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezepte/add',
      component: RezeptCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezepte/edit',
      component: RezeptEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/bezuege',
      component: BezuegeListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/bezuege/add',
      component: BezugCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/bezuege/edit',
      component: BezugEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezeptkategorien',
      component: RezeptkategorienListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezeptkategorien/add',
      component: RezeptkategorieCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezeptkategorien/edit',
      component: RezeptkategorieEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/zutatengruppen',
      component: ZutatengruppenListing,
      exact: true,
    },

    {
      path: '/controlpanel/rezeptdatenbank/zutatengruppen/add',
      component: ZutatengruppeCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/zutatengruppen/edit',
      component: ZutatengruppeEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezepte/verkaufsinformation/edit',
      component: VerkaufsinformationEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/benutzergruppen',
      component: GruppenListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/benutzergruppen/add',
      component: GruppeCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/benutzergruppen/edit',
      component: GruppeEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezeptgruppen',
      component: RezeptgruppenListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezeptgruppen/add',
      component: RezeptgruppeCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rezeptgruppen/edit',
      component: RezeptgruppeEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/produktkategorien',
      component: ProduktkategorienListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/produktkategorien/add',
      component: ProduktkategorieCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/produktkategorien/edit',
      component: ProduktkategorieEdit,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rohstoffkategorien',
      component: RohstoffkategorienListing,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rohstoffkategorien/add',
      component: RohstoffkategorieCreate,
      exact: true,
    },
    {
      path: '/controlpanel/rezeptdatenbank/rohstoffkategorien/edit',
      component: RohstoffkategorieEdit,
      exact: true,
    },
  ];
  return config;
}
